import { MouseEvent, useContext, useEffect, useRef, useState } from "react"
import { ESystemEmailType, TSystemEmailsTable } from "./types"
import { SystemEmailsContainerContext } from "../../context"
import { Link, Stack } from "@mui/material"
import {
    Button,
    copyToClipboard,
    DataTable,
    DialogHandler,
    EApiLanguages,
    EmptyContent,
    fetchRoute,
    GenericWrapperContext,
    Icon,
    LoadingContainer,
    SubNav,
    SystemEmailsFactory,
    TDataTableColumnsData,
    TDataTableRowData,
    TNumStr,
    TPromisePoolPartialSender,
    TPromisePoolPartialTemplates,
    TPromisePoolRef,
    TPromisePoolReturnData,
    TSystemEmail,
    Typography
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import "./styles.scss"
import { systemEmailSenderPromissePool, systemEmailTemplatesPromissePool } from "../../../../utils/promisesPools"
import { showSystemEmailDialog } from "../edit"
import { ERoutes } from "../../../../utils/url"
import { NameTableCol } from "../../../../components/tables/nameTableCol"

export default function SystemEmailsTable({ loading, reloadTable }: TSystemEmailsTable) {
    const genericWrapper = useContext(GenericWrapperContext)
    const account = genericWrapper.account
    const isPartnerOrOrganization = genericWrapper.account?.isPartnerOrOrganization()
    const systemEmailsContainerContext = useContext(SystemEmailsContainerContext)
    const { t } = useTranslation()
    const [tab, setTab] = useState<ESystemEmailType>(!isPartnerOrOrganization ? ESystemEmailType.contactDOptIn : ESystemEmailType.accountConfirm)

    const [partialTemplates, setPartialTemplates] = useState<TPromisePoolReturnData<TPromisePoolPartialTemplates>>({})
    const partialTemplatesPromiseRef = useRef<TPromisePoolRef>(undefined)

    const [partialSenders, setPartialSenders] = useState<TPromisePoolReturnData<TPromisePoolPartialSender>>({})
    const partialSendersPromiseRef = useRef<TPromisePoolRef>(undefined)

    const systemEmails = systemEmailsContainerContext?.systemEmails

    useEffect(() => {
        if (systemEmailsContainerContext?.systemEmails) {
            stopPromisePools()
            let systemEmails: TSystemEmail[] = []
            for (const [key, values] of Object.entries(systemEmailsContainerContext?.systemEmails)) {
                if (Array.isArray(values)) {
                    systemEmails = systemEmails.concat(values)
                }
            }
            systemEmailTemplatesPromissePool({ data: systemEmails, setter: setPartialTemplates, poolRef: partialTemplatesPromiseRef })
            systemEmailSenderPromissePool({ data: systemEmails, setter: setPartialSenders, poolRef: partialSendersPromiseRef })
        }
    }, [systemEmailsContainerContext?.systemEmails])

    useEffect(() => {
        return () => {
            stopPromisePools()
        }
    }, [])

    function stopPromisePools() {
        try {
            partialTemplatesPromiseRef.current?.stop()
            partialTemplatesPromiseRef.current = undefined

            partialSendersPromiseRef.current?.stop()
            partialSendersPromiseRef.current = undefined
        } catch (e) {//pass }
        }
    }


    function handleChangeEmailView(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        const sysName: ESystemEmailType = item.name as ESystemEmailType
        systemEmailsContainerContext.currentTab.current = sysName
        setTab(sysName)
    }

    function handleRowClick(event: React.MouseEvent<unknown>, row: TDataTableRowData) {
        const sysEmail = systemEmails?.[tab]?.find((sysEmail) => {
            return sysEmail.language === row.language && sysEmail.template?.id === row.template
        })

        if (sysEmail) {
            handleSystemEmailEdit(sysEmail)
        }
    }

    function handleSystemEmailEdit(sysEmail: TSystemEmail) {
        if (account)
            showSystemEmailDialog({
                account: account,
                action: "edit",
                initLanguage: sysEmail.language,
                initSenderId: sysEmail.sender?.id,
                initSysEmailName: tab,
                initSystemEmails: systemEmails,
                initTemplateId: sysEmail.template?.id,
                partnerBrand: genericWrapper.partnerBrand,

                onSuccess: () => { reloadTable() },
                onError: ({ msg }) => {
                    DialogHandler.show({
                        title: t(msg || "error-system-emails-update"),
                        type: "error"
                    })
                },
            })
    }

    function handleCopyClipBoard(id: TNumStr) {
        copyToClipboard(String(id))
        DialogHandler.show({ title: t("copied-clipboard") })
    }

    function handleDeleteSystemEmail(locale: EApiLanguages, sysEmailIndex: number) {
        const systemEmailsLeft = systemEmails?.[tab]?.filter((sysEmail, index) => {
            return sysEmail.language !== locale && index !== sysEmailIndex
        })

        SystemEmailsFactory.update({
            systemEmails: {
                [tab]: systemEmailsLeft
            }
        })
            .then(() => {
                reloadTable()
            })
            .catch(({ msg }) => {
                DialogHandler.show({
                    title: t(msg || "error-system-emails-delete"),
                    type: "error"
                })
            })
    }

    const colsData: TDataTableColumnsData[] = [
        {
            id: "template",
            label: t("general-name"),
            width: 525,
            sortable: true,
        },
        {
            id: "sender",
            label: t("general-sender_one"),
            sortable: true,
        },
        {
            id: "language",
            label: t("general-language"),
            sortable: true,
        },
        {
            id: "actions",
            label: ""
        }
    ]

    //@ts-expect-error
    const rows: any[] = systemEmails?.[tab] && systemEmails[tab].map((sysEmail: TSystemEmail, index) => {
        const partialTemplate: Partial<TPromisePoolPartialTemplates> = sysEmail.template?.id ? partialTemplates?.[sysEmail.template.id] : {}
        const partialSender: Partial<TPromisePoolPartialSender> = sysEmail.sender?.id ? partialSenders?.[sysEmail.sender.id] : {}
        //sysEmail.sender.id,
        return {
            language: sysEmail.language,
            sender: {
                id: sysEmail.sender?.id,
                content: sysEmail.sender?.id && <NameTableCol
                    name={partialSender?.name}
                    desc={partialSender?.email}
                    extra={<Typography
                        onClick={() => {
                            handleCopyClipBoard(sysEmail.sender?.id || "")
                        }}
                        variant="body2S"
                        className="uppercase">
                        {t("sender-details-id-copy") + ` ${sysEmail.sender?.id}`} <Icon name="Copy14" color="secondary" />
                    </Typography>}
                /> || <></>,
            },
            template: {
                id: sysEmail.template?.id,
                content: sysEmail.template?.id && <NameTableCol
                    name={partialTemplate?.name}
                    desc={partialTemplate?.description}
                    imgSrc={partialTemplate?.thumbnail_url}
                    truncateDescSize={200}
                    extra={<>
                        <Typography
                            onClick={() => {
                                handleCopyClipBoard(sysEmail.template?.id || "")
                            }}
                            variant="body2S"
                            className="uppercase">
                            {t("template-details-asset-id") + ` ${sysEmail.template?.id}`} <Icon name="Copy14" color="secondary" />
                        </Typography>
                        <Link href={"#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: sysEmail.template?.id } })}>{t("general-edit-template")}</Link>
                    </>}
                /> || <></>
            },
            actions: <>
                <Button
                    onClick={() => { handleSystemEmailEdit(sysEmail) }}
                    size="small"
                    color="body1"
                    variant="outlined"
                >{t("general-edit")}</Button>
                <Button
                    iconOnly
                    onClick={() => { handleDeleteSystemEmail(sysEmail.language, index) }}
                    size="small"
                    color="body1"
                    variant="outlined"
                ><Icon name="Delete18" /></Button>
            </>
        }
    }) || []



    let subNavItems = [
        { text: t("system-email-tab-account_confirm"), name: ESystemEmailType.accountConfirm },
        { text: t("system-email-tab-user_confirm"), name: ESystemEmailType.userConfirm },
        { text: t("system-email-tab-sender_confirm"), name: ESystemEmailType.senderConfirm },
        { text: t("system-email-tab-password_reset"), name: ESystemEmailType.passwordReset },
        { text: t("system-email-tab-contact_double_opt_in"), name: ESystemEmailType.contactDOptIn }
    ]

    if (!isPartnerOrOrganization) {
        subNavItems = [{ text: t("system-email-tab-contact_double_opt_in"), name: ESystemEmailType.contactDOptIn }]
    }


    return <Stack className={`systemEmailsTable ${loading ? "loading" : ""}`}>
        <SubNav
            className={loading ? "loading" : ""}
            onClick={handleChangeEmailView}
            initialSelected={tab}
            items={subNavItems}
        />


        <LoadingContainer loading={loading}>
            {rows.length > 0 &&
                <DataTable
                    onRowClick={handleRowClick}
                    columnsData={colsData}
                    rows={rows}
                    showPagination={false}
                />
            }

            {rows.length === 0 &&
                <EmptyContent
                    title={t("system-email-table-empty-title")}
                    desc={t("system-email-table-empty-desc")}
                />
            }

        </LoadingContainer>
    </Stack>
}